import React, { useState } from 'react';
import { Autocomplete, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { EmptyState, InlineMessage, LoadingState, SectionCard, StatusChip } from '../ui';
import type { StatusTone } from '../ui';
import { useToast } from '../../contexts/ToastContext';
import { tokens } from '../../theme/tokens';

type EdgeType = 'compatible_lens' | 'compatible_accessory';

interface AdminEdge {
  type: EdgeType;
  targetProductId: string;
  targetName?: string;
  targetProductType?: string;
  source: string;
  via?: string;
  removed: boolean;
  confidence?: number;
}

interface ProductOption {
  id: string;
  name: string;
  brand?: string;
  productType?: string;
}

const api = async (path: string, init: RequestInit = {}) => {
  const response = await makeApiCall(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()), ...(init.headers || {}) },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.success === false) throw new Error(body?.error || body?.message || 'Request failed');
  return body;
};

export const fetchCompatibilityEdges = async (productId: string): Promise<AdminEdge[]> =>
  (await api(`/normalization/canonical/${encodeURIComponent(productId)}/compatibility`)).data ?? [];

const sourceTone = (edge: AdminEdge): StatusTone =>
  edge.removed ? 'problem' : edge.source === 'manual' ? 'positive' : 'neutral';

const sourceLabel = (edge: AdminEdge): string =>
  edge.removed ? 'Removed by admin' : edge.source === 'manual' ? 'Added by admin' : 'Rule';

/**
 * Admin override queue for one canonical product's compatibility edges
 * (`productAssociations`, types `compatible_lens` / `compatible_accessory`).
 * Rule edges are recomputed nightly; an add or a removal here is a `manual`
 * document the recompute never touches.
 */
export function CompatibilityEdgesPanel({ productId }: { productId: string }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const queryKey = ['admin-compatibility-edges', productId];
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<ProductOption | null>(null);
  const [addType, setAddType] = useState<EdgeType>('compatible_lens');

  const edges = useQuery({
    queryKey,
    queryFn: () => fetchCompatibilityEdges(productId),
  });

  const options = useQuery({
    queryKey: ['admin-compatibility-search', search],
    enabled: search.trim().length >= 2,
    queryFn: async (): Promise<ProductOption[]> => {
      const params = new URLSearchParams({ search: search.trim(), limit: '8', offset: '0' });
      const body = await api(`/normalization/canonical?${params}`);
      return (body.data ?? []).map((p: any) => ({ id: p.id, name: p.name || p.model, brand: p.brand, productType: p.productType }));
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey });
  const fail = (error: unknown) => showToast(error instanceof Error ? error.message : 'Request failed', 'error');

  const add = useMutation({
    mutationFn: () =>
      api(`/normalization/canonical/${encodeURIComponent(productId)}/compatibility`, {
        method: 'POST',
        body: JSON.stringify({ targetProductId: picked?.id, type: addType }),
      }),
    onSuccess: () => {
      setPicked(null);
      setSearch('');
      showToast('Compatibility added', 'success');
      refresh();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: ({ edge, restore }: { edge: AdminEdge; restore: boolean }) =>
      api(
        `/normalization/canonical/${encodeURIComponent(productId)}/compatibility/${encodeURIComponent(edge.targetProductId)}?type=${edge.type}${restore ? '&restore=true' : ''}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_d, vars) => {
      showToast(vars.restore ? 'Restored; the rule may bring it back on the next recompute' : 'Removed', 'success');
      refresh();
    },
    onError: fail,
  });

  const recompute = useMutation({
    mutationFn: () => api('/normalization/compatibility/run', { method: 'POST', body: JSON.stringify({ productId }) }),
    onSuccess: (body) => {
      showToast(`Recomputed: ${body.data?.created ?? 0} added, ${body.data?.pruned ?? 0} removed`, 'success');
      refresh();
    },
    onError: fail,
  });

  return (
    <SectionCard
      dense
      padded
      title="Compatibility"
      subtitle="Lens, camera and accessory edges. A manual add or removal survives every recompute."
      actions={
        <Button size="small" variant="outlined" onClick={() => recompute.mutate()} disabled={recompute.isPending}>
          Recompute
        </Button>
      }
    >
      {edges.isLoading ? (
        <LoadingState size="inline" />
      ) : edges.isError ? (
        <InlineMessage severity="error">{(edges.error as Error).message}</InlineMessage>
      ) : (edges.data ?? []).length === 0 ? (
        <EmptyState size="compact" title="No compatibility edges yet" description="Run Recompute after the mount or specs are set, or add one below." />
      ) : (
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          {(edges.data ?? []).map((edge) => (
            <Box
              key={`${edge.type}-${edge.targetProductId}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: `1px solid ${tokens.line.hairline}` }}
            >
              <Box sx={{ flex: 1, minWidth: 0, opacity: edge.removed ? 0.6 : 1 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{edge.targetName || edge.targetProductId}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[edge.targetProductType, edge.via?.replace(/_/g, ' ')].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <StatusChip label={sourceLabel(edge)} tone={sourceTone(edge)} />
              <Button
                size="small"
                disabled={remove.isPending}
                onClick={() => remove.mutate({ edge, restore: edge.removed })}
              >
                {edge.removed ? 'Restore' : 'Remove'}
              </Button>
            </Box>
          ))}
        </Stack>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
        <Autocomplete
          size="small"
          sx={{ flex: 1, minWidth: 0 }}
          options={options.data ?? []}
          loading={options.isFetching}
          value={picked}
          onChange={(_e, value) => setPicked(value)}
          inputValue={search}
          onInputChange={(_e, value) => setSearch(value)}
          getOptionLabel={(o) => `${o.name}${o.brand ? ` (${o.brand})` : ''}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterOptions={(x) => x}
          renderInput={(params) => <TextField {...params} label="Find a product to link" />}
        />
        <TextField select size="small" label="As" value={addType} onChange={(e) => setAddType(e.target.value as EdgeType)} sx={{ minWidth: 140 }}>
          <MenuItem value="compatible_lens">Lens / camera</MenuItem>
          <MenuItem value="compatible_accessory">Accessory</MenuItem>
        </TextField>
        <Button variant="contained" disabled={!picked || add.isPending} onClick={() => add.mutate()}>
          Add
        </Button>
      </Stack>
    </SectionCard>
  );
}
